import { useState } from 'react';
import { createTag } from '../api/tags';

const PRESET_COLORS = [
  '#0d6efd', '#6610f2', '#6f42c1', '#d63384',
  '#dc3545', '#fd7e14', '#ffc107', '#198754',
  '#20c997', '#0dcaf0', '#6c757d', '#212529',
];

/**
 * Props:
 *   tags        – full list of available tags (array of Tag objects)
 *   selectedIds – array of selected tag IDs
 *   onToggle    – fn(tagId) called when a tag is toggled
 *   onTagCreated – fn(tag) called after a new tag is created (to update parent tag list)
 */
export default function TagSelector({ tags, selectedIds = [], onToggle, onTagCreated }) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#0d6efd');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleCreate() {
    if (!newName.trim()) return;
    setSaving(true);
    setError('');
    try {
      const tag = await createTag({ name: newName.trim(), color: newColor });
      onTagCreated(tag);
      onToggle(tag.id); // automatically select the new tag
      setNewName('');
      setNewColor('#0d6efd');
      setShowCreate(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {tags.map((t) => {
          const selected = selectedIds.includes(t.id);
          return (
            <button
              key={t.id}
              type="button"
              className={`btn btn-sm ${selected ? 'text-white' : 'btn-outline-secondary'}`}
              style={selected ? { backgroundColor: t.color, borderColor: t.color } : {}}
              onClick={() => onToggle(t.id)}
            >
              {t.name}
            </button>
          );
        })}
        <button
          type="button"
          className="btn btn-sm btn-outline-dashed border-secondary text-secondary"
          style={{ borderStyle: 'dashed' }}
          onClick={() => setShowCreate((v) => !v)}
          title="Neuen Tag erstellen"
        >
          <i className={`bi ${showCreate ? 'bi-x' : 'bi-plus'}`} />
          {!showCreate && ' Neuer Tag'}
        </button>
      </div>

      {showCreate && (
        <div
          className="border rounded p-3 mt-1"
          style={{ maxWidth: 380, background: 'var(--color-surface-2)' }}
        >
          <div className="fw-semibold small mb-2 text-muted">Neuen Tag erstellen</div>

          {error && <div className="alert alert-danger py-1 small mb-2">{error}</div>}

          <div className="mb-2">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Tag-Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleCreate())}
              autoFocus
            />
          </div>

          <div className="mb-3">
            <div className="small text-muted mb-1">Farbe</div>
            <div className="d-flex flex-wrap gap-1 mb-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="rounded-circle border-0 p-0"
                  style={{
                    width: 22,
                    height: 22,
                    backgroundColor: c,
                    outline: newColor === c ? '2px solid #000' : 'none',
                    outlineOffset: 2,
                  }}
                  onClick={() => setNewColor(c)}
                  title={c}
                />
              ))}
            </div>
            <div className="d-flex align-items-center gap-2">
              <input
                type="color"
                className="form-control form-control-color form-control-sm"
                style={{ width: 38, height: 30 }}
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                title="Eigene Farbe"
              />
              <span className="small text-muted">oder eigene Farbe wählen</span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={saving || !newName.trim()}
              onClick={handleCreate}
            >
              {saving ? <span className="spinner-border spinner-border-sm" /> : 'Erstellen'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => { setShowCreate(false); setError(''); }}
            >
              Abbrechen
            </button>
            {newName && (
              <span
                className="badge ms-auto"
                style={{ backgroundColor: newColor, color: '#fff' }}
              >
                {newName}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
